import { useBridge } from '@web/adapter/useBridgeRuntime'
import { api } from '@web/api/client'
import { StructuredValue } from '@web/components/tools/StructuredContent'
import type { PermissionRequest } from '@web/state/bridge'
import { useState } from 'react'

/** ACP permission descriptions and subjects never mutate the displayed tool. */
export function PermissionCard({ request }: { request: PermissionRequest }) {
  const { snapshot, sessionId } = useBridge()
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')
  if (request.resolved)
    return (
      <p className="mt-2 text-xs text-muted">
        {request.resolved.behavior === 'allow'
          ? 'Allowed'
          : request.resolved.behavior === 'cancelled'
            ? 'Withdrawn'
            : 'Denied'}
      </p>
    )
  const respond = async (optionId: string) => {
    setBusy(true)
    setError('')
    try {
      await api.command(sessionId, {
        type: 'permission',
        request_id: request.requestId,
        option_id: optionId,
      })
      setSent(true)
    } catch (failure) {
      setError((failure as Error).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <div
      className="mt-2 rounded-control border border-warn/50 bg-warn/10 p-3"
      data-permission-id={request.requestId}
    >
      <h3 className="text-sm font-semibold">
        {request.title ?? 'Permission requested'}
      </h3>
      {request.description && (
        <p className="mt-1 text-xs text-muted">{request.description}</p>
      )}
      {request.subject && (
        <details className="mt-2 text-xs">
          <summary>Operation details</summary>
          <StructuredValue value={request.subject} />
        </details>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        {request.options?.map((option) => (
          <button
            key={option.optionId}
            className={`btn btn-sm ${option.kind.startsWith('allow') ? 'btn-primary' : ''}`}
            disabled={
              busy ||
              sent ||
              snapshot.bridge.record?.migration_status === 'diagnostic' ||
              snapshot.hydrating ||
              snapshot.socket !== 'open'
            }
            onClick={() => void respond(option.optionId)}
          >
            {option.name}
          </button>
        ))}
      </div>
      {sent && (
        <p role="status" className="mt-2 text-xs">
          Response sent. Waiting for the agent…
        </p>
      )}
      {error && (
        <p role="alert" className="mt-2 text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  )
}
